import { useMemo, useState } from "react";
import { Calendar, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MeasurementLogEventDisplay } from "../MeasurementRequestDetails.types";
import { EVENT_TYPE_CONFIG } from "../MeasurementRequestDetails.values";
import { formatLogEventMessage, getCollapsedMessage } from "../MeasurementRequestDetails.functions";
import { formatTimestamp } from "@/utils/timeFormatting";

export function LogEventItem({ event }: { event: MeasurementLogEventDisplay }) {
  const [expanded, setExpanded] = useState(false);

  const config =
    EVENT_TYPE_CONFIG[event.type as keyof typeof EVENT_TYPE_CONFIG] ?? EVENT_TYPE_CONFIG.INFO;

  const Icon = config.icon;

  const formattedMessage = useMemo(() => formatLogEventMessage(event.message), [event.message]);

  const collapsedMessage = useMemo(() => getCollapsedMessage(formattedMessage), [formattedMessage]);

  const isLongMessage = collapsedMessage.isTruncated;

  const displayedMessage = expanded ? formattedMessage : collapsedMessage.message;

  return (
    <div className={`border rounded-lg p-4 space-y-3 transition-colors ${config.container}`}>
      <div className="flex items-start gap-3">
        <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${config.iconClassName}`} />

        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span
              className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${config.badge}`}
            >
              {event.type}
            </span>

            {isLongMessage && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpanded((value) => !value)}
                className="h-7 px-2 text-gray-500 hover:text-gray-700 flex-shrink-0"
                type="button"
              >
                {expanded ? (
                  <>
                    <ChevronUp className="h-3.5 w-3.5 mr-1" />
                    Collapse
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3.5 w-3.5 mr-1" />
                    Expand
                  </>
                )}
              </Button>
            )}
          </div>

          <p
            className={`text-sm text-gray-900 whitespace-pre-wrap break-words ${
              expanded ? "font-mono text-xs leading-relaxed" : ""
            }`}
          >
            {displayedMessage}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-4 text-gray-500 font-mono">
        <span className="flex items-end gap-1">
          <Calendar className="w-4 h-4" />
          <span className="text-xs leading-4">{formatTimestamp(event.timestamp)}</span>
        </span>
      </div>
    </div>
  );
}
